import { pathOr, paths, max, clamp, path, sum } from 'ramda';

import {
  getProductPackName,
  hasProductPackIngredients,
  isProductPackSerum,
  isProductPackEqual,
  getProductPackUniqueId,
  getProductPackReference
} from '@/models/core/ProductPack';
import { getProductPacks } from '@/models/back-end/ProductListBuilder';
import { getComplementaryProductDataFromTreatmentDto } from '@/models/back-end/Treatment';
import {
  addProductOptionsOption,
  getProductOptionsName,
  getProductOptionsProductPacksList,
  isProductOptionsEmpty
} from '@/models/core/ProductOptions';
import {
  CartItem,
  getCartItemName,
  getCartItemPrice,
  getCartItemProductPack,
  getCartItemQuantity
} from '@/models/core/CartItem';

import { types } from '@/store/types';
import { DYSFUNCTION_NAMES } from '@/constants/dysfunctions';
import { DEFAUL_CURRENCY } from '@/constants/currency';
import { DELIVERY_TYPES } from '@/constants/shipping';
import {
  MIN_PRICE_DELIVERY_FIELD_PATHS,
  PAGE_SECTION_NAME,
  MAIN_PAGE_SECTIONS
} from '@/constants/common';
import { PRODUCT_NAMES, SERUM_PRODUCT_NAMES, PRODUCT_SIZE_OPTIONS } from '@/constants/treatment';
import { COUNTRY_ISO, COUNTRIES_WITH_PAY_LATER_OFFERS } from '@/constants/countries';
import {
  addPrices,
  dividePrice,
  getPriceValue,
  isPriceLess,
  multiplyPrice,
  Price,
  subtractPrices
} from '@/models/core/values';
import { getGenericFormulaIds } from '@/models/back-end/EyeSerumGenericFormulas';

export const getters = {
  [types.getters.DOCTOR_FULL_NAME](state) {
    return pathOr('', [types.state.INITIAL_PAGE_DATA, 'doctorFullName'], state);
  },

  [types.getters.TREATMENT_ID](state) {
    return pathOr(null, [types.state.INITIAL_PAGE_DATA, 'treatmentId'], state);
  },

  [types.getters.ORDER_ID](state) {
    return pathOr(null, [types.state.TREATMENT_DATA, 'orderId'], state);
  },

  [types.getters.SCORES](state) {
    const { [types.state.DYSFUNCTIONS_DATA]: dysfunctionsData } = state;

    if (!dysfunctionsData) {
      return {};
    }

    return Object.values(DYSFUNCTION_NAMES).reduce((acc, dysfunctionName) => {
      const { score = 0 } = dysfunctionsData[dysfunctionName] || {};
      acc[dysfunctionName] = score;

      return acc;
    }, {});
  },

  [types.getters.HAS_DYSFUNCTIONS](state, storeGetters) {
    const { [types.getters.SCORES]: scores } = storeGetters;

    return Object.values(scores).some(score => score > 0);
  },

  [types.getters.GET_IS_PRODUCT_RECOMMENDED]: state => productName => {
    const { [types.state.RECOMMENDED_PRODUCT_NAMES]: recommendedProductNames } = state;

    return recommendedProductNames.includes(productName);
  },

  [types.getters.HAS_INGREDIENTS_IN_SERUMS](state, storeGetters) {
    const {
      [types.getters.ALL_PRODUCT_OPTIONS_SETS]: products,
      [types.getters.DOES_TREATMENT_CONTAIN_EYE_SERUM_GENERIC]: isContainsEyeSerumGeneric
    } = storeGetters;

    const faceOptionsSet = products[PRODUCT_NAMES.FACE_SERUM];
    const eyeOptionsSet = products[PRODUCT_NAMES.EYE_CONTOUR_SERUM];

    const facePackHasIngredients =
      !isProductOptionsEmpty(faceOptionsSet) &&
      hasProductPackIngredients(getProductOptionsProductPacksList(faceOptionsSet)[0]);

    const eyePackHasIngredients =
      !isContainsEyeSerumGeneric &&
      !isProductOptionsEmpty(eyeOptionsSet) &&
      hasProductPackIngredients(getProductOptionsProductPacksList(eyeOptionsSet)[0]);

    return facePackHasIngredients || eyePackHasIngredients;
  },

  [types.getters.CURRENCY](state) {
    return pathOr(DEFAUL_CURRENCY, [types.state.PRICES, 'currencyCode'], state);
  },

  [types.getters.IS_ANY_SERUM_RECOMMENDED](state) {
    const { [types.state.RECOMMENDED_PRODUCT_NAMES]: recommendedProducts } = state;
    return recommendedProducts.some(productName => SERUM_PRODUCT_NAMES.includes(productName));
  },

  [types.getters.MAIN_PAGE_SECTIONS](state, storeGetters) {
    const {
      [types.getters.HAS_INGREDIENTS_IN_SERUMS]: hasIngredientsInSerums,
      [types.getters.HAS_DYSFUNCTIONS]: hasDysfunctions,
      [types.getters.IS_ANY_SERUM_RECOMMENDED]: isAnySerumRecommended
    } = storeGetters;

    return MAIN_PAGE_SECTIONS.filter(({ sectionName }) => {
      if (sectionName === PAGE_SECTION_NAME.YOUR_TREATMENT) {
        return isAnySerumRecommended;
      }

      if (sectionName === PAGE_SECTION_NAME.YOUR_INGREDIENTS) {
        return hasIngredientsInSerums;
      }

      if (PAGE_SECTION_NAME.YOUR_SKIN === sectionName) {
        return hasDysfunctions;
      }

      return true;
    });
  },

  [types.getters.HAS_PAYPAL_PAY_LATER_OPTION]: (_, storeGetters) => {
    const { [types.getters.DELIVERY_COUNTRY]: deliveryCountry } = storeGetters;

    return COUNTRIES_WITH_PAY_LATER_OFFERS.some(
      country => country.toLowerCase() === deliveryCountry
    );
  },

  /* FREE DELIVERY */

  [types.getters.DELIVERY_COUNTRY](state) {
    return pathOr('', [types.state.TREATMENT_DATA, 'customerCountryCode'], state).toLowerCase();
  },

  [types.getters.MIN_DELIVERY_PRICE](state) {
    const {
      [types.state.DELIVERY_DATA]: deliveryData,
      [types.state.DELIVERY_TYPE]: deliveryType
    } = state;

    const [
      minPriceForClassicNoSerum,
      minPriceForExpressNoSerum,
      minPriceForClassicWithSerum,
      minPriceForExpressWithSerum
    ] = paths(MIN_PRICE_DELIVERY_FIELD_PATHS, deliveryData);

    if (deliveryType === DELIVERY_TYPES.CLASSIC) {
      return {
        minDeliveryPriceWithNoSerum: minPriceForClassicNoSerum || 0,
        minDeliveryPriceWithSerum: minPriceForClassicWithSerum || 0
      };
    }

    return {
      minDeliveryPriceWithNoSerum: minPriceForExpressNoSerum || 0,
      minDeliveryPriceWithSerum: minPriceForExpressWithSerum || 0
    };
  },

  [types.getters.CART_PRICE_RESIDUE_BY_SERUM_INCLUSION](_, storeGetters) {
    const {
      [types.getters.MIN_DELIVERY_PRICE]: minDeliveryPrice,
      [types.getters.CART_PRICE]: cartPrice
    } = storeGetters;

    const { minDeliveryPriceWithNoSerum, minDeliveryPriceWithSerum } = minDeliveryPrice;

    return {
      cartPriceResidueWithNoSerum: Math.ceil(
        minDeliveryPriceWithNoSerum - getPriceValue(cartPrice)
      ),
      cartPriceResidueWithSerum: Math.ceil(minDeliveryPriceWithSerum - getPriceValue(cartPrice))
    };
  },

  [types.getters.CART_PRICE_RESIDUE](_, storeGetters) {
    const {
      [types.getters.CART_PRICE_RESIDUE_BY_SERUM_INCLUSION]: cartPriceResidueBySerumInclusion,
      [types.getters.HAS_SERUM_IN_CART]: hasSerumInCart
    } = storeGetters;

    const {
      cartPriceResidueWithNoSerum,
      cartPriceResidueWithSerum
    } = cartPriceResidueBySerumInclusion;

    return hasSerumInCart ? cartPriceResidueWithSerum : cartPriceResidueWithNoSerum;
  },

  [types.getters.HAS_FREE_DELIVERY](_, storeGetters) {
    const { [types.getters.CART_PRICE_RESIDUE]: cartPriceResidue } = storeGetters;
    return cartPriceResidue <= 0;
  },

  [types.getters.FREE_DELIVERY_MESSAGE_CONFIG](_, storeGetters) {
    const {
      [types.getters.HAS_FREE_DELIVERY]: hasFreeDelivery,
      [types.getters.CART_PRICE_RESIDUE_BY_SERUM_INCLUSION]: cartPriceResidueBySerumInclusion,
      [types.getters.IS_ANY_SERUM_RECOMMENDED]: isAnySerumRecommended,
      [types.getters.HAS_SERUM_IN_CART]: hasSerumInCart,
      [types.getters.CART_PRICE_RESIDUE]: cartPriceResidue,
      [types.getters.DELIVERY_COUNTRY]: deliveryCountry,
      [types.getters.CURRENCY]: currency
    } = storeGetters;

    if (hasFreeDelivery) {
      return null;
    }

    const {
      cartPriceResidueWithNoSerum,
      cartPriceResidueWithSerum
    } = cartPriceResidueBySerumInclusion;

    const priceValue =
      isAnySerumRecommended && !hasSerumInCart
        ? max(cartPriceResidueWithNoSerum, cartPriceResidueWithSerum)
        : cartPriceResidue;
    const residue = Price(priceValue, currency);

    return {
      messageKey: 'cart.freeDeliveryMessage',
      deliveryData: { residue, deliveryCountry }
    };
  },

  [types.getters.GET_FREE_DELIVERY_MESSAGE_CONFIG]: (_, storeGetters) => () => {
    return storeGetters[types.getters.FREE_DELIVERY_MESSAGE_CONFIG];
  },

  [types.getters.CLASSIC_DELIVERY_DATA]: (state, storeGetters) => {
    const { [types.state.DELIVERY_DATA]: deliveryData } = state;
    const {
      [types.getters.HAS_SERUM_IN_CART]: hasSerumInCart,
      [types.getters.CURRENCY]: currency
    } = storeGetters;

    if (!deliveryData) {
      return null;
    }

    const {
      billedDeliveryPriceClassicNoSerum,
      billedDeliveryPriceClassicWithSerum,
      classicDeliveryDays: deliveryDays
    } = deliveryData;

    const deliveryPriceValue = hasSerumInCart
      ? billedDeliveryPriceClassicWithSerum
      : billedDeliveryPriceClassicNoSerum;
    const deliveryPrice = Price(deliveryPriceValue, currency);

    return { deliveryDays, deliveryPrice };
  },

  [types.getters.EXPRESS_DELIVERY_DATA]: (state, storeGetters) => {
    const { [types.state.DELIVERY_DATA]: deliveryData } = state;
    const {
      [types.getters.HAS_SERUM_IN_CART]: hasSerumInCart,
      [types.getters.CURRENCY]: currency
    } = storeGetters;

    if (!deliveryData) {
      return null;
    }

    const {
      billedDeliveryPriceExpressNoSerum,
      billedDeliveryPriceExpressWithSerum,
      expressDeliveryDays: deliveryDays
    } = deliveryData;

    const deliveryPriceValue = hasSerumInCart
      ? billedDeliveryPriceExpressWithSerum
      : billedDeliveryPriceExpressNoSerum;
    const deliveryPrice = Price(deliveryPriceValue, currency);

    return { deliveryDays, deliveryPrice };
  },

  [types.getters.AVAILABLE_DELIVERY_TYPES]: (state, storeGetters) => {
    const { [types.state.DELIVERY_DATA]: deliveryData } = state;
    const { [types.getters.DELIVERY_COUNTRY]: deliveryCountry } = storeGetters;

    if (deliveryCountry.toLowerCase() === COUNTRY_ISO.MEXICO.toLowerCase()) {
      return [DELIVERY_TYPES.FIXED];
    }

    const availableDeliveryTypes = [];
    const { classicDeliveryDays, expressDeliveryDays } = deliveryData || {};

    if (classicDeliveryDays) {
      availableDeliveryTypes.push(DELIVERY_TYPES.CLASSIC);
    }

    if (expressDeliveryDays) {
      availableDeliveryTypes.push(DELIVERY_TYPES.EXPRESS);
    }

    return availableDeliveryTypes;
  },

  [types.getters.DELIVERY_COST](state, storeGetters) {
    const { [types.state.DELIVERY_TYPE]: deliveryType } = state;
    const {
      [types.getters.EXPRESS_DELIVERY_DATA]: expressDeliveryData,
      [types.getters.CLASSIC_DELIVERY_DATA]: classicDeliveryData,
      [types.getters.CURRENCY]: currency
    } = storeGetters;
    const deliveryData = {
      [DELIVERY_TYPES.CLASSIC]: classicDeliveryData,
      [DELIVERY_TYPES.EXPRESS]: expressDeliveryData
    };

    const priceValue = pathOr(0, [deliveryType, 'deliveryPrice'], deliveryData);

    return Price(priceValue, currency);
  },

  /* CART */

  [types.getters.CART_PRODUCTS_COUNT](state) {
    const { [types.state.CART]: cart } = state;

    return sum(cart.map(({ quantity }) => quantity));
  },

  [types.getters.GET_IS_PRODUCT_ADDED_TO_CART_BY_NAME]: (state, storeGetters) => productName => {
    const { [types.getters.CART_ITEMS]: cartItems } = storeGetters;

    return cartItems.some(cartItem => getCartItemName(cartItem) === productName);
  },

  [types.getters.GET_IS_PRODUCT_PACK_ADDED_TO_CART]: (state, storeGetters) => productPack => {
    const { [types.getters.CART_ITEMS]: cartItems } = storeGetters;

    return cartItems.some(cartItem =>
      isProductPackEqual(getCartItemProductPack(cartItem), productPack)
    );
  },

  [types.getters.CART_PRICE](state, storeGetters) {
    const {
      [types.getters.CURRENCY]: currency,
      [types.getters.CART_ITEMS]: cartItems
    } = storeGetters;

    return cartItems.reduce((acc, cartItem) => {
      const price = getCartItemPrice(cartItem);
      const quantity = getCartItemQuantity(cartItem);

      const totalCartItemPrice = multiplyPrice(price, quantity);

      return addPrices(acc, totalCartItemPrice || Price(0, currency));
    }, Price(0, currency));
  },

  [types.getters.HAS_SERUM_IN_CART](state, storeGetters) {
    const { [types.getters.CART_ITEMS]: cartItems } = storeGetters;

    return cartItems.some(cartItem => isProductPackSerum(getCartItemProductPack(cartItem)));
  },

  [types.getters.IS_MULTICOUNTRY_DELIVERY_AVAILABLE](state) {
    const availableDeliveryCountries = state[types.state.AVAILABLE_DELIVERY_COUNTRIES];

    return availableDeliveryCountries && availableDeliveryCountries.length > 1;
  },

  [types.getters.TOTAL_ORDER_COST]: (state, storeGetters) => {
    const { [types.state.PROMO_CODE_DATA]: promoCodeData } = state;
    const {
      [types.getters.TOTAL_COST_WITHOUT_DISCOUNT]: totalCostWithoutDiscount,
      [types.getters.CURRENCY]: currency
    } = storeGetters;

    if (promoCodeData) {
      const { amount: amountValue, amountType } = promoCodeData;
      const amount = Price(amountValue, currency);

      const promoCodeAmount =
        amountType === 'ABSOLUTE'
          ? amount
          : multiplyPrice(totalCostWithoutDiscount, dividePrice(amount, 100));

      const priceValue = clamp(
        0,
        Infinity,
        getPriceValue(subtractPrices(totalCostWithoutDiscount, promoCodeAmount))
      );

      return Price(priceValue, currency);
    }

    return totalCostWithoutDiscount;
  },

  [types.getters.TOTAL_COST_WITHOUT_DISCOUNT]: (state, storeGetters) => {
    const {
      [types.getters.CART_PRICE]: cartPrice,
      [types.getters.DELIVERY_COST]: deliveryCost
    } = storeGetters;

    return addPrices(cartPrice, deliveryCost);
  },

  [types.getters.GET_IF_CUSTOM_FEE_WARNING_MUST_BE_SHOWN]: state => ({
    shouldDisplayTotalCost
  }) => {
    const {
      [types.state.DELIVERY_DATA]: deliveryData,
      [types.state.DELIVERY_TYPE]: deliveryType
    } = state;

    if (!deliveryData) {
      return false;
    }

    const {
      shouldShowClassicCustomsClearanceFeeWarning,
      shouldShowExpressCustomsClearanceFeeWarning
    } = deliveryData;

    const hasClassicCustomsClearanceFeeWarning =
      shouldShowClassicCustomsClearanceFeeWarning && deliveryType === 'classic';
    const hasExpressCustomsClearanceFeeWarning =
      shouldShowExpressCustomsClearanceFeeWarning && deliveryType === 'express';

    return (
      (hasClassicCustomsClearanceFeeWarning || hasExpressCustomsClearanceFeeWarning) &&
      shouldDisplayTotalCost
    );
  },

  [types.getters.GET_SHIPPING_PAYMENT_INFO]: (state, storeGetters) => ({
    shouldDisplayTotalCost
  }) => {
    const {
      [types.state.DELIVERY_DATA]: deliveryData,
      [types.state.DELIVERY_TYPE]: deliveryType
    } = state;

    const {
      [types.getters.CART_PRICE]: cartPrice,
      [types.getters.DELIVERY_COUNTRY]: deliveryCountry,
      [types.getters.TOTAL_ORDER_COST]: totalCost,
      [types.getters.CURRENCY]: currency
    } = storeGetters;

    const shippingInfo = {};

    // subtotal calculation

    if (!deliveryData) {
      shippingInfo.subtotal = cartPrice;
      shippingInfo.totalCost = totalCost;

      return shippingInfo;
    }

    const hasGreatBritainDelivery =
      deliveryCountry.toLowerCase() === COUNTRY_ISO.GREAT_BRITAIN.toLowerCase();

    const {
      productsPriceWithVAT,
      shouldShowClassicCustomsClearanceFeeWarning,
      shouldShowExpressCustomsClearanceFeeWarning,
      expressTotalToPay,
      classicTotalToPay
    } = deliveryData;

    shippingInfo.subtotal =
      hasGreatBritainDelivery && shouldDisplayTotalCost
        ? Price(productsPriceWithVAT, currency)
        : cartPrice;

    // order vat calculation

    shippingInfo.orderVAT =
      deliveryType === 'classic'
        ? Price(path(['classicArrivalVatValue'], deliveryData), currency)
        : Price(path(['expressArrivalVatValue'], deliveryData), currency);

    const SWIZZ_TAX_FREE_PRICE = Price(65, currency);

    const hasSwitzerlandFreeCustomFee =
      deliveryCountry === COUNTRY_ISO.SWITZERLAND.toLowerCase() &&
      isPriceLess(totalCost, SWIZZ_TAX_FREE_PRICE);

    if (hasSwitzerlandFreeCustomFee) {
      shippingInfo.orderVAT = Price(0, currency);
    }

    // order total calculation

    const hasClassicTotalToPay =
      deliveryType === 'classic' && shouldShowClassicCustomsClearanceFeeWarning;
    const hasExpressTotalToPay =
      deliveryType === 'express' && shouldShowExpressCustomsClearanceFeeWarning;

    if (!hasClassicTotalToPay && !hasExpressTotalToPay) {
      shippingInfo.totalCost = totalCost;
    }

    if (hasClassicTotalToPay) {
      shippingInfo.totalCost = Price(classicTotalToPay, currency);
    }

    if (hasExpressTotalToPay) {
      shippingInfo.totalCost = Price(expressTotalToPay, currency);
    }

    return shippingInfo;
  },

  [types.getters.FOOTER_LINKS](state) {
    const { [types.state.LOCALE]: locale } = state;

    return {
      legal: `https://www.skinxs.com/legal/${locale}`,
      termsOfUse: `https://www.skinxs.com/terms/${locale}`,
      termsOfSales: `https://skinxs.com/terms-of-sales/${locale}`,
      privacyNoticeDoctors: `https://www.skinxs.com/doctor-notice/${locale}`,
      privacyNoticePatients: `https://www.skinxs.com/patient-notice/${locale}`
    };
  },

  [types.getters.DOES_TREATMENT_CONTAIN_EYE_SERUM_GENERIC](state, storeGetters) {
    const { [types.getters.GET_IS_PRODUCT_RECOMMENDED]: getIsProductRecommended } = storeGetters;

    return !getIsProductRecommended(PRODUCT_NAMES.EYE_CONTOUR_SERUM);
  },

  [types.getters.ALL_PRODUCT_OPTIONS_SETS](state) {
    const {
      [types.state.TREATMENT_DATA]: treatmentData,
      [types.state.STOCK]: stock,
      [types.state.PRICES]: prices,
      [types.state.EYE_CONTOUR_SERUM_GENERIC_FORMULAS_LIST]: eyeSerumGenericFormulas
    } = state;

    if (!treatmentData || !stock || !prices) {
      return {};
    }

    const packs = getProductPacks(treatmentData, stock, prices, eyeSerumGenericFormulas);

    return packs.reduce((acc, pack) => {
      const name = getProductPackName(pack);
      acc[name] = addProductOptionsOption(pack, acc[name]);

      return acc;
    }, {});
  },

  [types.getters.ALL_PRODUCT_PACKS_LIST](state, storeGetters) {
    const { [types.getters.ALL_PRODUCT_OPTIONS_SETS]: allProductOptionsSets } = storeGetters;

    return Object.entries(allProductOptionsSets).flatMap(([, productOptions]) => {
      return getProductOptionsProductPacksList(productOptions);
    });
  },

  [types.getters.RECOMMENDED_PRODUCT_OPTIONS_SETS](state, storeGetters) {
    const {
      [types.state.RECOMMENDED_PRODUCT_NAMES]: recommendedProductNames,
      [types.state.TREATMENT_DATA]: treatmentData
    } = state;
    const { [types.getters.ALL_PRODUCT_OPTIONS_SETS]: allOptions } = storeGetters;

    return Object.values(allOptions).reduce((acc, productOptions) => {
      const name = getProductOptionsName(productOptions);

      if (recommendedProductNames.includes(name)) {
        if (PRODUCT_SIZE_OPTIONS[name]) {
          const options = getProductOptionsProductPacksList(productOptions);
          const recommendedProductOption = options.filter(option =>
            getComplementaryProductDataFromTreatmentDto(option, treatmentData)
          );

          acc[name] = { productName: name, options: recommendedProductOption };
        } else {
          acc[name] = productOptions;
        }
      }

      return acc;
    }, {});
  },

  [types.getters.GET_PRODUCT_FROM_CART]: (state, storeGetters) => productName => {
    const { [types.getters.CART_ITEMS]: cartItems } = storeGetters;

    const cartItem = cartItems.find(item => getCartItemName(item) === productName);

    return cartItem ? getCartItemProductPack(cartItem) : null;
  },

  [types.getters.EYE_SERUM_GENERIC_FORMULA_IDS](state) {
    const { [types.state.EYE_CONTOUR_SERUM_GENERIC_FORMULAS_LIST]: formulas } = state;

    if (!formulas) {
      return [];
    }

    return getGenericFormulaIds(formulas);
  },

  [types.getters.CART_ITEMS](state, storeGetters) {
    const { [types.state.CART]: cart } = state;
    const { [types.getters.ALL_PRODUCT_PACKS_LIST]: allProductPacksList } = storeGetters;

    return cart
      .map(itemDescription => {
        const { id, quantity } = itemDescription;
        const pack = allProductPacksList.find(
          productPack => getProductPackUniqueId(productPack) === id
        );

        if (!pack) {
          return null;
        }

        return CartItem(pack, quantity);
      })
      .filter(item => !!item);
  },

  [types.getters.GET_PRODUCT_PACK_BY_REFERENCE]: (state, storeGetters) => productReference => {
    const { [types.getters.ALL_PRODUCT_PACKS_LIST]: allProductPacksList } = storeGetters;

    return allProductPacksList.find(
      productPack => getProductPackReference(productPack) === productReference
    );
  },

  [types.getters.LOCALE_WITH_REGION]: (state, storeGetters) => {
    const { [types.state.LOCALE]: locale } = state;
    const { [types.getters.DELIVERY_COUNTRY]: deliveryCountry } = storeGetters;

    const { baseName: localeWithRegion } = new Intl.Locale(locale, {
      region: deliveryCountry
    });

    return localeWithRegion;
  }
};
